// MonthCalendarView.js - With Meeting Room support
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { Typography, IconButton, Grid, Tooltip, Button, CircularProgress, Box, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import BoltIcon from '@mui/icons-material/Bolt';
import { getVenuePrice } from 'api';
import {
  CalendarContainer, CalendarHeader, WeekDayHeader, WeekRow, CalendarCell, DateNumber, PriceTag,
  InstantBookingIcon, WeekDaysGrid, OriginalPrice, CurrentPrice, BookingSummaryContainer, CommunityIndicator
} from './styled';

const MonthCalendarView = ({ 
  slug, 
  onDateSelect, 
  defaultDate, 
  selectedPrice, 
  spaces, 
  preparingBooking, 
  doBooking,
  priceDataLoading = false,
  showBookingSummary = false,
  isMeetingRoom = false, // New prop to determine if this is a meeting room
  fixedSlotPrice = null, // New prop for meeting room fixed price
  workingDays = [1, 2, 3, 4, 5], // Mon-Fri by default
  communityBookingsByDate = {}, // Community bookings data by date
  onCommunityIndicatorClick = null, // Handler when clicking community indicator
}) => {
  // Use defaultDate if provided, otherwise use the current month without selecting any date
  const [currentMonth, setCurrentMonth] = useState(defaultDate ? defaultDate : dayjs().startOf('month'));
  const [prices, setPrices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    defaultDate ? defaultDate.format('YYYY-MM-DD') : null
  );
  
  // A cache to store generated price objects for meeting rooms to maintain stable references
  const priceObjectCache = useRef(new Map());
  
  // Function to get or create a price object with a stable reference
  const getOrCreatePriceObject = useCallback((dateStr) => {
    if (!isMeetingRoom || fixedSlotPrice === null) return null;
    
    // If the price object is already in the cache, return it
    if (priceObjectCache.current.has(dateStr)) {
      return priceObjectCache.current.get(dateStr);
    }
    
    // Otherwise create a new one and store it in the cache
    const newPriceObject = {
      start: dateStr,
      price: fixedSlotPrice.toString(),
      price_vat: (fixedSlotPrice * 1.2).toString(),
      original_price: fixedSlotPrice,
      available: true,
      instant_booking: true
    };
    
    priceObjectCache.current.set(dateStr, newPriceObject);
    return newPriceObject;
  }, [isMeetingRoom, fixedSlotPrice]);
  
  // Clear the cache when the month changes to prevent memory leaks
  useEffect(() => {
    priceObjectCache.current.clear();
  }, [currentMonth.format('YYYY-MM')]);
  
  const [loadingPrices, setLoadingPrices] = useState(false);
  const theme = useTheme();
  
  const isToday = (date) => date.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');

  // Effect to update currentMonth and selectedDate when defaultDate changes
  useEffect(() => {
    if (defaultDate) {
      setCurrentMonth(defaultDate.startOf('month'));
      setSelectedDate(defaultDate.format('YYYY-MM-DD'));
    }
  }, [defaultDate]);

  // Effect to synchronize selectedDate with selectedPrice
  useEffect(() => {
    if (selectedPrice && selectedPrice.start) {
      const priceDate = dayjs(selectedPrice.start).format('YYYY-MM-DD');
      setSelectedDate(priceDate);
      
      // Ensure we're viewing the month containing the selected date
      const priceMonth = dayjs(selectedPrice.start).startOf('month');
      if (priceMonth.month() !== currentMonth.month() || 
          priceMonth.year() !== currentMonth.year()) {
        setCurrentMonth(priceMonth);
      }
    }
  // Remove currentMonth from dependencies to prevent circular updates
  }, [selectedPrice]);

  // Effect to generate fixed prices for meeting rooms
  useEffect(() => {
    if (isMeetingRoom && fixedSlotPrice !== null) {
      setLoadingPrices(true);
      // Generate prices for the entire calendar view
      const startOfView = currentMonth.startOf('month').startOf('week');
      const endOfView = currentMonth.endOf('month').endOf('week');
      
      const generatedPrices = [];
      let day = startOfView.clone();
      
      while (day.isBefore(endOfView) || day.isSame(endOfView, 'day')) {
        // Only create price objects for working days
        if (workingDays.includes(day.day() || 7)) {
          // Skip past dates
          const today = dayjs().startOf('day');
          if (day.isSame(today) || day.isAfter(today)) {
            generatedPrices.push({
              start: day.format('YYYY-MM-DD'),
              price: fixedSlotPrice.toString(),
              price_vat: (fixedSlotPrice * 1.2).toString(), // Assuming 20% VAT
              original_price: fixedSlotPrice, // No discount for meeting rooms
              available: true,
              instant_booking: true // You can adjust this based on your business logic
            });
          }
        }
        day = day.add(1, 'day');
      }
      
      // Use functional update to prevent stale state issues
      setPrices(prevPrices => {
        // Only update if prices have actually changed
        const pricesHaveChanged = JSON.stringify(prevPrices) !== JSON.stringify(generatedPrices);
        return pricesHaveChanged ? generatedPrices : prevPrices;
      });
      setLoadingPrices(false);
    }
  // Memoize format function call to avoid triggering effect unnecessarily
  }, [currentMonth.format('YYYY-MM'), isMeetingRoom, fixedSlotPrice, workingDays]);

  // Effect to fetch prices for the current month view - only if not a meeting room
  useEffect(() => {
    // Skip price fetching for meeting rooms with fixed price
    if (isMeetingRoom && fixedSlotPrice !== null) return;
    
    let isMounted = true; // Track component mount state
    
    const fetchPrices = async () => {
      setLoadingPrices(true);
      try {
        const startOfView = currentMonth.startOf('month').startOf('week');
        const endOfView = currentMonth.endOf('month').endOf('week');
        const response = await getVenuePrice(slug, {
          start: startOfView.format('YYYY-MM-DD'),
          end: endOfView.format('YYYY-MM-DD')
        });
        
        // Only update state if component is still mounted
        if (isMounted && response && response.prices) {
          setPrices(response.prices);
        }
      } catch (error) {
        console.error('Error fetching prices:', error);
      } finally {
        if (isMounted) {
          setLoadingPrices(false);
        }
      }
    };

    fetchPrices();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  // Use a stable reference to currentMonth with format applied
  }, [slug, currentMonth.format('YYYY-MM'), isMeetingRoom, fixedSlotPrice]);

  const getLowestPrice = React.useMemo(() => {
    // For meeting rooms, all prices are the same
    if (isMeetingRoom && fixedSlotPrice !== null) return fixedSlotPrice;
    
    if (!prices.length) return null;

    // Filter out prices that are 0 or less
    const validPrices = prices.filter(p => parseFloat(p.price) > 0);

    // Return null if there are no valid prices
    if (validPrices.length === 0) return null;

    // Find the minimum price among valid prices
    const lowestPrice = Math.min(...validPrices.map(p => parseFloat(p.price)));
    return lowestPrice;
  // Only recalculate when prices array changes or meeting room options change
  }, [prices, isMeetingRoom, fixedSlotPrice]);

  const isPriceLowest = (price, originalPrice) => {
    // For meeting rooms, no price is highlighted as "lowest"
    if (isMeetingRoom && fixedSlotPrice !== null) return false;
    
    const currentPrice = parseFloat(price);
    return currentPrice === getLowestPrice && currentPrice < originalPrice;
  };

  const isPriceLower = (price, originalPrice) => {
    // For meeting rooms, no price is highlighted as "lower"
    if (isMeetingRoom && fixedSlotPrice !== null) return false;
    
    const currentPrice = parseFloat(price);
    return currentPrice < originalPrice && currentPrice !== getLowestPrice;
  };

  const weeks = React.useMemo(() => {
    const start = currentMonth.startOf('month').startOf('week');
    const end = currentMonth.endOf('month').endOf('week');
    const days = [];
    let week = [];

    let day = start;
    while (day.isBefore(end)) {
      week.push(day);
      if (week.length === 7) {
        days.push(week);
        week = [];
      }
      day = day.add(1, 'day');
    }
    return days;
  // Only recalculate when the month format changes, not on every render
  }, [currentMonth.format('YYYY-MM')]);

  const handleDateSelect = (date, priceObject) => {
    // For meeting rooms with fixed price
    if (isMeetingRoom && fixedSlotPrice !== null) {
      // Create a price object if none exists
      if (!priceObject) {
        const dateObj = dayjs(date);
        // Only allow booking on working days
        if (workingDays.includes(dateObj.day() || 7)) {
          const today = dayjs().startOf('day');
          if (dateObj.isSame(today) || dateObj.isAfter(today)) {
            // Use our cache system for stable references
            const cachedPriceObject = getOrCreatePriceObject(date);
            setSelectedDate(date);
            onDateSelect(cachedPriceObject);
          }
        }
      } else {
        setSelectedDate(date);
        onDateSelect(priceObject);
      }
    } else {
      // Original logic for hot desks
      if (priceObject?.available !== '-' && parseFloat(priceObject?.price) > 0) {
        setSelectedDate(date);
        onDateSelect(priceObject);
      }
    }
  };

  const isClickable = (date, priceObject) => {
    const today = dayjs().startOf('day');
    const dateToCheck = dayjs(date).startOf('day');
    
    // For meeting rooms with fixed price
    if (isMeetingRoom && fixedSlotPrice !== null) {
      // Date is clickable if:
      // 1. It's today or in the future
      // 2. It's a working day
      return (dateToCheck.isSame(today) || dateToCheck.isAfter(today)) &&
        workingDays.includes(dateToCheck.day() || 7);
    }
    
    // Original logic for hot desks
    // Day is clickable if:
    // 1. It's today or in the future
    // 2. It has available slots
    // 3. The price is greater than 0
    return (dateToCheck.isSame(today) || dateToCheck.isAfter(today)) &&
      priceObject?.available !== '-' &&
      parseFloat(priceObject?.price) > 0;
  };

  // Helper to get the appropriate price object for a given date
  // Memoize this function to avoid recreating price objects on every render
  const getPriceObjectForDate = React.useCallback((dateStr) => {
    // For meeting rooms with fixed price
    if (isMeetingRoom && fixedSlotPrice !== null) {
      // First check if the date already has a price object in our array
      const existingPrice = prices.find(p => dayjs(p.start).format('YYYY-MM-DD') === dateStr);
      if (existingPrice) return existingPrice;
      
      // Otherwise, check if it's a valid day for booking
      const date = dayjs(dateStr);
      const today = dayjs().startOf('day');
      if (workingDays.includes(date.day() || 7) && (date.isSame(today) || date.isAfter(today))) {
        // Create a new price object on-the-fly
        // Use the same object reference for the same date to prevent unnecessary re-renders
        return getOrCreatePriceObject(dateStr);
      }
      return null;
    }
    
    // Original logic for hot desks
    // First check if the selectedPrice prop matches this date
    if (selectedPrice && dayjs(selectedPrice.start).format('YYYY-MM-DD') === dateStr) {
      return selectedPrice;
    }
    
    // Otherwise use the price from the internal prices array
    return prices.find(p => dayjs(p.start).format('YYYY-MM-DD') === dateStr);
  }, [isMeetingRoom, fixedSlotPrice, workingDays, prices, selectedPrice, getOrCreatePriceObject]);

  const calculateStripeFee = (priceWithVAT) => {
    // UK card rate: 1.4% + £0.20
    const ukCardFee = Math.max(
      priceWithVAT * 0.014 + 0.20,  // Percentage fee + fixed fee
      0.20  // Minimum fee
    );

    // Round to nearest 5p for simplicity
    return Math.ceil(ukCardFee * 20) / 20;
  };

  // Helper to determine the booking status message
  const getBookingStatusMessage = () => {
    // During price data loading, show loading message
    if (priceDataLoading) {
      return "Loading availability...";
    }
    
    if (selectedDate) {
      if (dayjs(selectedDate).isBefore(dayjs(), 'day')) {
        return 'Cannot book dates in the past';
      } else if (selectedPrice) {
        // For meeting rooms, we don't check spaces
        if (isMeetingRoom && fixedSlotPrice !== null) {
          return 'Ready to book';
        } else if (selectedPrice.available < spaces) {
          return 'Not enough spaces available';
        } else if (parseFloat(selectedPrice.price) <= 0) {
          return 'No spaces available for this date';
        } else {
          return 'Ready to book';
        }
      } else {
        return 'This date is not available for booking';
      }
    } else {
      return 'Please select a date';
    }
  };

  // Calculate the actual price based on the selected price object and spaces
  const calculateActualPrice = () => {
    if (!selectedPrice) return { basePrice: 0, priceWithVAT: 0 };
    
    const basePrice = parseFloat(selectedPrice.price) * spaces;
    const priceWithVAT = parseFloat(selectedPrice.price_vat) * spaces;
    
    return { basePrice, priceWithVAT };
  };

  const { basePrice, priceWithVAT } = calculateActualPrice();

  return (
    <CalendarContainer>
      <CalendarHeader>
        <IconButton onClick={() => setCurrentMonth(m => m.subtract(1, 'month'))}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6">{currentMonth.format('MMMM YYYY')}</Typography>
        <IconButton onClick={() => setCurrentMonth(m => m.add(1, 'month'))}>
          <ChevronRightIcon />
        </IconButton>
      </CalendarHeader>

      <WeekDaysGrid container>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
          <Grid item key={day}>
            <WeekDayHeader>{day}</WeekDayHeader>
          </Grid>
        ))}
      </WeekDaysGrid>

      {loadingPrices ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '300px' 
        }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading calendar...
          </Typography>
        </Box>
      ) : (
        <>
          {weeks.map((week, weekIndex) => (
            <WeekRow key={weekIndex}>
              {week.map(date => {
                const dateStr = date.format('YYYY-MM-DD');
                // Use the helper function to get the price object for this date
                const priceObject = getPriceObjectForDate(dateStr);
                const isCurrentMonth = date.month() === currentMonth.month();
                
                // For meeting rooms with fixed price
                const hasAvailability = isMeetingRoom && fixedSlotPrice !== null 
                  ? !!priceObject // If we have a price object, it's available
                  : priceObject?.available > 0;
                
                const hasValidPrice = priceObject && parseFloat(priceObject.price) > 0;
                const today = dayjs().startOf('day');
                const dateToCheck = dayjs(dateStr).startOf('day');
                const isPastDate = dateToCheck.isBefore(today);
                
                // A date is selected if it matches the local selectedDate OR
                // if it matches the date in the selectedPrice prop from parent
                const isSelected = selectedDate === dateStr || 
                  (selectedPrice && dayjs(selectedPrice.start).format('YYYY-MM-DD') === dateStr);

                // Check for community bookings on this date
                const communityActivity = communityBookingsByDate[dateStr];
                const hasCommunityActivity = communityActivity && communityActivity.count > 0;

                return (
                  <CalendarCell
                    isPast={isPastDate}
                    key={dateStr}
                    isCurrentMonth={isCurrentMonth}
                    isSelected={isSelected}
                    hasAvailability={isClickable(dateStr, priceObject)}
                    onClick={() => isClickable(dateStr, priceObject) && handleDateSelect(dateStr, priceObject)}
                  >
                    {/* Community activity indicator */}
                    {hasCommunityActivity && (
                      <Tooltip 
                        title={`${communityActivity.count} community member${communityActivity.count > 1 ? 's' : ''} ${communityActivity.communities?.length > 0 ? `from ${communityActivity.communities.join(', ')}` : ''}`}
                        arrow
                      >
                        <CommunityIndicator 
                          onClick={(e) => {
                            e.stopPropagation();
                            onCommunityIndicatorClick?.(dateStr, communityActivity);
                          }}
                        />
                      </Tooltip>
                    )}

                    {/* For meeting rooms, show instant booking icon on all available dates */}
                    {(isMeetingRoom && fixedSlotPrice !== null && isClickable(dateStr, priceObject)) || 
                      (priceObject?.instant_booking && hasValidPrice) ? (
                      <InstantBookingIcon>
                        <Tooltip title="Instant confirmation booking">
                          <BoltIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        </Tooltip>
                      </InstantBookingIcon>
                    ) : null}

                    <DateNumber
                      isCurrentMonth={isCurrentMonth}
                      isToday={isToday(date)}
                    >
                      {date.format('D')}
                    </DateNumber>

                    {/* For meeting rooms with fixed price */}
                    {isMeetingRoom && fixedSlotPrice !== null && isClickable(dateStr, priceObject) ? (
                      <PriceTag>
                        <CurrentPrice>
                          {fixedSlotPrice.toLocaleString('en-GB', {
                            style: 'currency',
                            currency: 'GBP',
                            minimumFractionDigits: 0
                          })}
                        </CurrentPrice>
                      </PriceTag>
                    ) : (
                      // Original logic for hot desks
                      priceObject && hasAvailability ? (
                        hasValidPrice ? (
                          <PriceTag>
                            {parseFloat(priceObject.price) < priceObject.original_price && (
                              <OriginalPrice>
                                {priceObject.original_price.toLocaleString('en-GB', {
                                  style: 'currency',
                                  currency: 'GBP',
                                  minimumFractionDigits: 0
                                })}
                              </OriginalPrice>
                            )}
                            <CurrentPrice
                              isLowest={isPriceLowest(priceObject.price, priceObject.original_price)}
                              isLower={isPriceLower(priceObject.price, priceObject.original_price)}
                            >
                              {(priceObject.price * 1).toLocaleString('en-GB', {
                                style: 'currency',
                                currency: 'GBP',
                                minimumFractionDigits: 0
                              })}
                            </CurrentPrice>
                          </PriceTag>
                        ) : (
                          <Typography
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              fontSize: '0.75rem',
                              color: 'text.secondary',
                              fontWeight: 500
                            }}
                          >
                            Full
                          </Typography>
                        )
                      ) : (
                        priceObject && (
                          <Typography
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              fontSize: '0.75rem',
                              color: 'text.secondary',
                              fontWeight: 500
                            }}
                          >
                            Full
                          </Typography>
                        )
                      )
                    )}
                  </CalendarCell>
                );
              })}
            </WeekRow>
          ))}
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 2, sm: 3 },
          mt: 2,
          px: 2,
          fontSize: '0.85rem',
          color: 'text.secondary',
          flexWrap: 'nowrap'
        }}
      >
        {/* For meeting rooms, show simplified legend */}
        {isMeetingRoom && fixedSlotPrice !== null ? (
          <Tooltip
            title="Book instantly without waiting for confirmation"
            arrow
            placement="top"
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'help'
            }}>
              <BoltIcon sx={{ fontSize: 16, color: 'warning.main' }} />
              Instant booking
            </Box>
          </Tooltip>
        ) : (
          // Original legend for hot desks
          <>
            <Tooltip
              title="Lower than the regular price - book now to save money!"
              arrow
              placement="top"
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'help',
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.green.sad
                }} />
                Discounted price
              </Box>
            </Tooltip>

            <Tooltip
              title="The most competitive price available this month"
              arrow
              placement="top"
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'help',
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main
                }} />
                Best price
              </Box>
            </Tooltip>

            <Tooltip
              title="Book instantly without waiting for confirmation"
              arrow
              placement="top"
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'help'
              }}>
                <BoltIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                Instant booking
              </Box>
            </Tooltip>
          </>
        )}
      </Box>

      {showBookingSummary && <BookingSummaryContainer>
        {/* For meeting rooms, show booking summary if a date is selected */}
        {isMeetingRoom && fixedSlotPrice !== null && selectedDate ? (
          <>
            <Box sx={{
              mb: 1,
              px: 2,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: 'action.hover',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="body2" color="text.secondary">
                Processing fee
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Typography
                    variant="body2"
                    sx={{
                      textDecoration: 'line-through',
                      color: 'text.secondary'
                    }}
                  >
                    {calculateStripeFee(fixedSlotPrice * 1.2).toLocaleString('en-GB', {
                      style: 'currency',
                      currency: 'GBP',
                    })}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="primary.main"
                    fontWeight={600}
                  >
                    £0.00
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mt: -0.5 }}>
                  VAT inc
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: 'background.paper',
              borderRadius: 1,
              boxShadow: 1
            }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={500}>
                  Meeting Room
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedDate ? dayjs(selectedDate).format('DD MMM YYYY') : ''}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end'
                }}>
                  <Typography variant="h5" color="primary.main" fontWeight={600}>
                    {fixedSlotPrice.toLocaleString('en-GB', {
                      style: 'currency',
                      currency: 'GBP',
                    })}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mt: -0.5,
                      fontSize: '0.7rem'
                    }}
                  >
                    {(fixedSlotPrice * 1.2).toLocaleString('en-GB', {
                      style: 'currency',
                      currency: 'GBP',
                    })} VAT inc.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="button_color"
                  onClick={doBooking}
                  disabled={preparingBooking}
                  sx={{
                    minWidth: { xs: '80px', sm: '100px' }
                  }}
                >
                  {preparingBooking ? <CircularProgress size={20} /> : 'Confirm'}
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          // Original logic for hot desks
          selectedPrice?.available >= spaces && parseFloat(selectedPrice?.price) > 0 ? (
            <>
              {selectedPrice?.available >= spaces && (
                <Box sx={{
                  mb: 1,
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: 'action.hover',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Processing fee
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: 'line-through',
                          color: 'text.secondary'
                        }}
                      >
                        {calculateStripeFee(priceWithVAT).toLocaleString('en-GB', {
                          style: 'currency',
                          currency: 'GBP',
                        })}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="primary.main"
                        fontWeight={600}
                      >
                        £0.00
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mt: -0.5 }}>
                      VAT inc
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                backgroundColor: 'background.paper',
                borderRadius: 1,
                boxShadow: 1
              }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={500}>
                    {spaces} {spaces > 1 ? 'Hot Desks' : 'Hot Desk'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedDate ? dayjs(selectedDate).format('DD MMM YYYY') : ''}
                  </Typography>
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end'
                  }}>
                    <Typography variant="h5" color="primary.main" fontWeight={600}>
                      {basePrice.toLocaleString('en-GB', {
                        style: 'currency',
                        currency: 'GBP',
                      })}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mt: -0.5,
                        fontSize: '0.7rem'
                      }}
                    >
                      {priceWithVAT.toLocaleString('en-GB', {
                        style: 'currency',
                        currency: 'GBP',
                      })} VAT inc.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="button_color"
                    onClick={doBooking}
                    disabled={preparingBooking}
                    sx={{
                      minWidth: { xs: '80px', sm: '100px' }
                    }}
                  >
                    {preparingBooking ? <CircularProgress size={20} /> : 'Confirm'}
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <Box className="booking-status">
              {priceDataLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  <Typography variant="body1">
                    Checking availability...
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body1">
                  {getBookingStatusMessage()}
                </Typography>
              )}
            </Box>
          )
        )}
        
        {/* Show instant booking notice only for hot desks */}
        {!isMeetingRoom && selectedPrice && parseFloat(selectedPrice.price) > 0 && !selectedPrice.instant_booking && (
          <Box sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{
              fontSize: '1.2rem',
              color: theme.palette.blue.sad,
              display: 'flex',
              alignItems: 'center'
            }}>
              <InfoIcon fontSize="inherit" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Your booking will need to be confirmed by the host. You&apos;ll receive notification once it&apos;s approved, typically within 24 hours.
            </Typography>
          </Box>
        )}
        
        {/* Show instant booking indicator for meeting rooms */}
        {isMeetingRoom && fixedSlotPrice !== null && selectedDate && (
          <Box sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{
              fontSize: '1.2rem',
              color: theme.palette.warning.main,
              display: 'flex',
              alignItems: 'center'
            }}>
              <BoltIcon fontSize="inherit" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Your meeting room booking will be instantly confirmed.
            </Typography>
          </Box>
        )}
      </BookingSummaryContainer>}
    </CalendarContainer>
  );
};

export default MonthCalendarView;